<template>
    <MonitorLayoutTemplate
        :table_title="table_title"
        :is_show_chart_dialog="is_show_chart_dialog"
    >
        <template #datetime>
            <QpeDatetime
                :props_date="date"
                :props_time="time"
                :name="default_value"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
            />
        </template>

        <template #description>
            <div class="rainfall__description">
                {{ $t('__rainfall.description_head') }} 
                <span :style="{'background-color': '#FFFF00'}">{{ $t('__rainfall.heavy_rain') }}</span>/ 
                <span :style="{'background-color': '#FFC000'}">{{ $t('__rainfall.extremely_heavy_rain') }}</span>/ 
                <span :style="{'background-color': '#FF0000'}">{{ $t('__rainfall.torrential_rain') }}</span>/ 
                <span :style="{'background-color': '#FF0000'}">{{ $t('__rainfall.extremely_torrential_rain') }}</span> 
                {{ $t('__rainfall.description_tail') }}
            </div>
        </template>

        <template #filter_condition_options>
            <MonitorOptionSet 
                ref="monitor_option_set"
                :option_set="option_set"
                @filter_data="filter_data"
            />
        </template>

        <template #data_table="slot_props">
            <QpeDatatable
                :data="table_data"
                :headers="header"
                :name="default_value"
                :error_text="error_text"
                :search="slot_props.search"
                :should_draw_diagram_header="should_draw_diagram_header"
                :enable_diagram_slot="true"
                :enable_table_label="true"
                :table_label_function="calc_accumulated_precp_level"
                :show_footer="!is_pub"
                :show_download_doc_btn="!is_pub"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
                @open-chart="assign_chart_component"
                :is_show_chart_dialog_parent.sync="is_show_chart_dialog"
            >
                <template #diagram_dialog="{ close_dialog }">
                    <GisChart v-if="is_show_chart_dialog"
                        id="belongs_monitor_chart"
                        :chart_options="chart_options"
                        :default_variable="$t('__rainfall.rainfall')"
                        :prop_rain_selected="current_table_header"
                        @close-chart-dialog="close_dialog"
                    />
                </template>
                <template #table_description>
                    <span>
                        {{ $t('__monitor.no_data_annotaion') }} 
                    </span>
                </template>
            </QpeDatatable>
        </template>
    </MonitorLayoutTemplate>
</template>

<script>
    import { redirect_monitor_table_ajax, monitor_table_ajax } from "@/utils/monitor/monitor.js"
    import { 
        sort_callback_by_city_name_tw, sort_callback_by_city_name_en, 
        sort_float_callback_by_given_key,
        calc_accumulated_precp_level 
    } from '@/utils/monitor/common_tool.js'
    import {
        generate_variable_options,
        combine_new_variable_options,
        update_option_set_from_local_storage,
        update_current_option_value,
        new_filter_unit_function
    } from '@/utils/monitor/option.js'
    import { get_stations_history_params } from "@/utils/chart/chart_ajax.js"
    import { filter_same_district } from '@/utils/monitor/customization/port.js'
    import { monitor_mixin } from "@/utils/monitor/monitor_mixin.js"
    import { TABLE_VALUE_TEXT_MAPPING } from "@/config/monitor_config.js"
    import { mapGetters, mapState } from "vuex"
    import MonitorLayoutTemplate from "@/components/monitor/_tool/layout_template.vue"
    import QpeDatetime from "@/components/monitor/qpe_datetime.vue"
    import MonitorOptionSet from "@/components/monitor/monitor_option_set.vue"
    import QpeDatatable from "@/components/monitor/qpe_datatable.vue"
    
    export default {
        name: 'Rainfall',

        components: {
            MonitorLayoutTemplate,
            QpeDatetime,
            MonitorOptionSet, 
            QpeDatatable, 
            GisChart: () => import('@/components/chart/gis_chart.vue')
        },

        mixins: [monitor_mixin],

        props: {
            default_value: {
                type: Number,
                required: true,
            },

            activate_filter_by_parent: {
                type: Boolean,
                default: false
            },

            filter_value_from_parent: {
                type: String,
                default: ''
            }
        },

        data() {
            return {
                header: [],
                table_data: [],
                error_text: '',

                // filter使用
                immutable_table_data: [],
                option_set: [],

                should_draw_diagram_header: [
                    '10分鐘', '10-Minute',
                    '1小時',  '1-Hour',
                    '3小時', '3-Hour',
                    '6小時', '6-Hour',
                    '12小時', '12-Hour',
                    '24小時', '24-Hour',
                    '48小時', '48-Hour',
                    '72小時', '72-Hour',
                ],

                is_show_chart_dialog: false,
                chart_options: {
                    type: { 'gauge': true }
                },
                current_table_header: '',
                
                calc_accumulated_precp_level: calc_accumulated_precp_level,

                local_storage_option_name: 'rainfall_option',
            }
        },
        
        computed: {
            ...mapState([
                'language',
                'client_name',
            ]),

            ...mapGetters([
                'is_pub'
            ]),

            table_title() {
                if (this.language === 'en') {
                    return `${this.$t('__rainfall.default_name')} Information`
                }
                return `${TABLE_VALUE_TEXT_MAPPING[this.default_value]['tag_name']}資訊`
            },

            display_name() {
                return this.$t('__rainfall.default_name')
            },
            
            display_all_city() {
                return this.$t('__rainfall.display_all_city')
            }, 
            
            display_all_township() {
                return this.$t('__rainfall.display_all_township')
            },
            
            init_interval() {
                return this.$t('__rainfall.init_interval')
            },
            
            init_precp() {
                return this.$t('__rainfall.init_precp')
            },

            intervals() {
                return [
                    this.$t('__rainfall.min_10'), this.$t('__rainfall.hour_1'),
                    this.$t('__rainfall.hour_3'), this.$t('__rainfall.hour_6'),
                    this.$t('__rainfall.hour_12'), this.$t('__rainfall.hour_24'),
                    this.$t('__rainfall.hour_48'), this.$t('__rainfall.hour_72'),
                ]
            },
            
            accumulated_precps() {
                return [this.$t('__rainfall.init_precp'), '>=0.5mm', '>=10mm', '>=15mm', '>=30mm', '>=50mm', '>=90mm', '>=100mm']
            },
        },
        
        watch: {
            immutable_table_data: {
                handler() {
                    if (Object.keys(this.immutable_table_data).length === 0) return
                    
                    if (this.activate_filter_by_parent) {
                        return this.$emit('collect_data', this.immutable_table_data)
                    }
                    this.generate_option_set()
                    this.option_set = update_option_set_from_local_storage(this.option_set , this.local_storage_option_name)
                },
                deep: true
            },

            language() {
                this.option_set = []
                monitor_table_ajax.call(this, this.default_value, `${this.date} ${this.time}:00`)
            },

            filter_value_from_parent() {
                this.table_data = this.sort_table_data(filter_same_district(this.immutable_table_data, this.filter_value_from_parent))
            }
        },

        created() {
            redirect_monitor_table_ajax.call(this, this.default_value)
        },

        methods: {
            generate_table_data(data, header) {
                const sort_callback = this.language === 'en' ? sort_callback_by_city_name_en : sort_callback_by_city_name_tw
                this.immutable_table_data = data.sort(sort_callback);
                this.table_data = this.sort_table_data(data);
                this.header = header;
            },

            generate_option_set() {
                const basic_variable_options = [
                    { default_value: this.display_all_city, json_key: this.$t('__rainfall.city'), label: 'city' },
                    { default_value: this.display_all_township, json_key: this.$t('__rainfall.township'), label: 'township' },
                ]

                const variable_options = generate_variable_options(basic_variable_options, this.immutable_table_data)
                
                if (this.option_set.length === 0) {
                    const fix_options = [
                        { current_option_value: this.init_interval, label: 'interval', option_array: this.intervals },
                        { current_option_value: this.init_precp, label: 'precps', option_array: this.accumulated_precps },
                    ]
    
                    this.option_set = [...variable_options, ...fix_options]
                } else {
                    this.option_set = combine_new_variable_options(this.option_set, variable_options)
                    this.$refs.monitor_option_set.change_option_set()
                }
            },

            filter_data(filtered_condition, change_condition_label) {
                const update_current_value_by_filtered_condition = update_current_option_value(filtered_condition)
                this.option_set = this.option_set.map(update_current_value_by_filtered_condition)

                // 縣市被改掉時，讓鄉鎮選項變成所有鄉鎮，過濾的需求也會被改動
                if (change_condition_label === 'city') {
                    this.option_set[1].current_option_value = this.display_all_township
                    filtered_condition['township'] = this.display_all_township
                }

                const filter_same_city = new_filter_unit_function(
                    filtered_condition['city'], 
                    this.$t('__rainfall.city'), 
                    this.display_all_city
                )
                const filter_same_township = new_filter_unit_function(
                    filtered_condition['township'], 
                    this.$t('__rainfall.township'), 
                    this.display_all_township
                )
                
                const filter_by_rain_standard = datum => {
                    const current_interval = filtered_condition['interval']
                    const compare_interval = datum[current_interval] === '--' 
                        ? 0 
                        : datum[current_interval]
                    const compare_accumulated_precp = filtered_condition['precps'] === this.$t('__rainfall.init_precp') 
                        ?  '' 
                        :  filtered_condition['precps']
                    return compare_interval >= Number(compare_accumulated_precp.slice(2, -2))
                }

                const filtered_data = this.immutable_table_data.filter((datum) => {
                    return (
                        filter_same_city(datum) &&
                        filter_same_township(datum) &&
                        filter_by_rain_standard(datum)
                    )
                })

                this.table_data = this.sort_table_data(filtered_data)

                // 過濾完之後重新根據資料狀態，生成鄉鎮 option
                this.generate_towns_options(filtered_condition['city'])

                // 把目前選擇的 option 存起來
                localStorage.setItem(this.local_storage_option_name, JSON.stringify(filtered_condition))
            },

            generate_towns_options(city) {                
                const filter_towns_by_city = item => item[this.$t('__rainfall.city')] ===  city
                const get_town = item => item[this.$t('__rainfall.township')]
                const filtered_towns_options = city === this.display_all_city 
                    ? this.immutable_table_data 
                    : this.immutable_table_data.filter(filter_towns_by_city)
                const towns_options = filtered_towns_options.map(get_town)
                this.option_set[1].option_array = [this.display_all_township, ...new Set(towns_options)]
            },

            assign_chart_component(header_name, choosed_value, current_row_data) {
                this.current_table_header = header_name
                this.chart_options = { ...this.chart_options, ...get_stations_history_params.call(this, current_row_data) }
            },

            sort_table_data(data){
                const sort_by_24h_precp = sort_float_callback_by_given_key(this.$t('__rainfall.hour_24'))
                return [...data].sort(sort_by_24h_precp)
            }
        },
    }
</script>

<style lang="scss" scoped>
    @import '../../../../css/mixin';

    .rainfall__description {
        max-width: 480px;
    }
</style>